import type { CatalogProvider } from "./integration-catalog.js";

export type OnboardingConnectionGroupKey =
  | "sales_orders"
  | "subscriptions"
  | "ad_spend"
  | "affiliate_attribution"
  | "payment_processing"
  | "fulfillment_shipping"
  | "email_sms"
  | "crm"
  | "accounting"
  | "website_analytics"
  | "other";

export interface OnboardingConnectionGroupMeta {
  key: OnboardingConnectionGroupKey;
  label: string;
  blurb: string;
  requirement: "Required" | "Required if used" | "Recommended" | "Optional" | "Review";
}

export const ONBOARDING_CONNECTION_GROUPS: readonly OnboardingConnectionGroupMeta[] = [
  { key: "sales_orders", label: "Sales, orders, and CRM", blurb: "Where completed orders, refunds, customers, products, and sales activity live.", requirement: "Required" },
  { key: "subscriptions", label: "Subscriptions", blurb: "Subscription status, churn, failed payments, and next billing dates.", requirement: "Required if used" },
  { key: "ad_spend", label: "Advertising", blurb: "What you spend on paid ads, broken out by channel and campaign.", requirement: "Required if used" },
  { key: "affiliate_attribution", label: "Affiliates and partnerships", blurb: "Affiliate sales, partner attribution, and commissions.", requirement: "Required if used" },
  { key: "payment_processing", label: "Payment processing", blurb: "Where card and PayPal fees come from. Your gateway and processor may have different names, so choose every name you recognize.", requirement: "Recommended" },
  { key: "fulfillment_shipping", label: "Fulfillment and shipping", blurb: "Postage and fulfillment costs tied to real shipments.", requirement: "Recommended" },
  { key: "accounting", label: "Accounting", blurb: "Overhead and other business expenses from your books.", requirement: "Recommended" },
  { key: "email_sms", label: "Email and SMS", blurb: "The tools that send email or text messages to your customers.", requirement: "Optional" },
  { key: "website_analytics", label: "Website analytics", blurb: "Traffic, landing pages, events, and funnel behavior.", requirement: "Optional" },
  { key: "other", label: "Other connections", blurb: "Anything else that holds useful business data.", requirement: "Review" },
];

const FULFILLMENT_PLATFORMS = new Set(["shipstation"]);

const REVIEW_WITH_US_PAYMENT_PROCESSORS: CatalogProvider[] = [
  {
    platform: "easy_pay_direct",
    category: "cost_actualization",
    label: "Easy Pay Direct",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "nmi",
    category: "cost_actualization",
    label: "NMI",
    description: "Usually the payment gateway. We’ll confirm the processor and review its real fees before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "authorize_net",
    category: "cost_actualization",
    label: "Authorize.net",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "braintree",
    category: "cost_actualization",
    label: "Braintree",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "square",
    category: "cost_actualization",
    label: "Square",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "shop_pay",
    category: "cost_actualization",
    label: "Shop Pay",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "payarc",
    category: "cost_actualization",
    label: "PayArc",
    description: "We will review its real fees and settlement timing with you before they affect your numbers.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
];

const REVIEW_WITH_US_CONNECTIONS: CatalogProvider[] = [
  {
    platform: "buygoods",
    category: "attribution",
    label: "BuyGoods",
    description: "Affiliate sales, commissions, refunds, and chargebacks. We’ll review its report or connection with you before the numbers are verified.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
  {
    platform: "jetpack_shipsmart",
    category: "cost_actualization",
    label: "Jetpack / ShipSmart",
    description: "Fulfillment and shipping costs. We’ll review its shipment report or connection with you before the numbers are verified.",
    required: false,
    comingSoon: true,
    reviewWithUs: true,
    connectKind: "coming_soon",
    showInOnboarding: true,
    syncActions: [],
  },
];

/**
 * Some systems matter during setup before we have a direct pull for them.
 * Keep the list here, not in the registry: these are onboarding disclosures,
 * never a claim that the platform is connected or changing the dashboard.
 */
export function addOnboardingReviewChoices(providers: CatalogProvider[]): CatalogProvider[] {
  const knownPlatforms = new Set(providers.map((provider) => provider.platform));
  return [
    ...providers,
    ...REVIEW_WITH_US_PAYMENT_PROCESSORS.filter((provider) => !knownPlatforms.has(provider.platform)),
    ...REVIEW_WITH_US_CONNECTIONS.filter((provider) => !knownPlatforms.has(provider.platform)),
  ];
}

function connectionNameKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * A platform that was missing during onboarding may become supported later.
 * Exact normalized matching lets the saved request clear when the client
 * selects that connection, without confusing similarly named tools.
 */
export function providerMatchesMissingPlatformRequest(provider: CatalogProvider, requestedName: string): boolean {
  const requestKey = connectionNameKey(requestedName);
  return requestKey.length > 0
    && (requestKey === connectionNameKey(provider.platform) || requestKey === connectionNameKey(provider.label));
}

export function isOrderSourceProvider(provider: CatalogProvider): boolean {
  // HubSpot can pull commerce orders. Other CRM and email tools currently only
  // receive approved customer fields, so they cannot satisfy the sales source.
  return provider.category === "store" || provider.platform === "hubspot";
}

export function onboardingGroupKeysForProvider(provider: CatalogProvider): OnboardingConnectionGroupKey[] {
  if (isOrderSourceProvider(provider)) return ["sales_orders"];
  if (provider.category === "subscription_state") return ["subscriptions"];
  if (provider.category === "ad_platform") return ["ad_spend"];
  if (provider.category === "attribution") return ["affiliate_attribution"];
  if (provider.category === "cost_actualization") {
    return [
      FULFILLMENT_PLATFORMS.has(provider.platform) || provider.platform === "jetpack_shipsmart"
        ? "fulfillment_shipping"
        : "payment_processing",
    ];
  }
  if (provider.category === "accounting") return ["accounting"];
  if (provider.category === "analytics") return ["website_analytics"];

  // Ontraport is both a CRM and an email tool. One checkmark must appear in
  // both places so a founder can find it the way their team thinks about it.
  const groups: OnboardingConnectionGroupKey[] = [];
  if (provider.isCrm || provider.category === "crm") groups.push("sales_orders");
  if (provider.isEsp || provider.category === "esp") groups.push("email_sms");
  return groups.length > 0 ? groups : ["other"];
}

export function groupOnboardingProviders(providers: CatalogProvider[]): Array<{
  meta: OnboardingConnectionGroupMeta;
  providers: CatalogProvider[];
}> {
  const grouped = new Map<OnboardingConnectionGroupKey, CatalogProvider[]>();
  for (const provider of providers) {
    if (provider.comingSoon && !provider.reviewWithUs) continue;
    for (const key of onboardingGroupKeysForProvider(provider)) {
      const list = grouped.get(key) ?? [];
      if (!list.some((item) => item.platform === provider.platform)) list.push(provider);
      grouped.set(key, list);
    }
  }
  return ONBOARDING_CONNECTION_GROUPS
    .map((meta) => ({
      meta,
      providers: (grouped.get(meta.key) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .filter((group) => group.providers.length > 0);
}
